import { Modal } from 'antd';

import MagicIconSVG from '../../assets/MagicIconSVG';
import InlineStyled from '../../CommonComponents/InlineStyled/InlineStyled';
import AppButton from '../../CommonComponents/UI/AppButton/AppButton';
import { WORKFLOW_INTRO_MODAL } from '../../Constants/commonConstants';
import { useTakeTour } from '../TakeTour/hooks/useTakeTour';
import WorkflowIntroStep11SVG from './assets/workflow-intro-step-1-1.svg';
import WorkflowIntroStep12SVG from './assets/workflow-intro-step-1-2.svg';
import WorkflowIntroStep2SVG from './assets/workflow-intro-step-2.svg';
import WorkflowIntroStep31SVG from './assets/workflow-intro-step-3-1.svg';
import WorkflowIntroStep32SVG from './assets/workflow-intro-step-3-2.svg';
import WorkflowIntroStep4SVG from './assets/workflow-intro-step-4.svg';

const oneTimeModal = () => {
  const userId = localStorage.getItem('user_id');

  let modalObj = {};

  try {
    modalObj = JSON.parse(localStorage.getItem(WORKFLOW_INTRO_MODAL)) || {};
  } catch (e) {
    modalObj = {};
  }

  modalObj[userId] = false;
  localStorage.setItem(WORKFLOW_INTRO_MODAL, JSON.stringify(modalObj));
};

const StepText = ({ children }) => {
  return (
    <InlineStyled
      $style={{
        color: '#1F3044',
        fontSize: 14,
      }}
    >
      {children}
    </InlineStyled>
  );
};

const workflowModalData = [
  {
    stepText: (
      <StepText>
        Go to{' '}
        <InlineStyled as='span' $style={{ color: '#1F3044', fontWeight: 600 }}>
          Profile &gt; Settings &gt; Workflows &gt;
        </InlineStyled>{' '}
        <InlineStyled as='span' $style={{ color: '#06F', fontWeight: 600 }}>
          Create New Workflow
        </InlineStyled>
        . <br /> Add your extraction prompt and save it as a workflow.
      </StepText>
    ),
    subText: (
      <StepText>
        You can also create a workflow directly from the Chat Window by clicking{' '}
        <InlineStyled as='span' $style={{ color: '#06F', fontWeight: 600 }}>
          Create New Workflow
        </InlineStyled>
        . <br /> This redirects you to{' '}
        <InlineStyled as='span' $style={{ color: '#1F3044', fontWeight: 600 }}>
          Settings
        </InlineStyled>
        , where you can complete the same workflow creation process.
      </StepText>
    ),
    image: <img src={WorkflowIntroStep11SVG} alt='step-1 of workflow' />,
    image2: <img src={WorkflowIntroStep12SVG} alt='step-1 of workflow' />,
  },
  {
    stepText: (
      <StepText>
        Open the{' '}
        <InlineStyled as='span' $style={{ color: '#06F', fontWeight: 600 }}>
          Workflow
        </InlineStyled>{' '}
        tab next to Chat.
      </StepText>
    ),
    image: <img src={WorkflowIntroStep2SVG} alt='step-2 of workflow' />,
  },
  {
    stepText: (
      <StepText>
        In the Chat Input, select either{' '}
        <InlineStyled as='span' $style={{ color: '#06F', fontWeight: 600 }}>
          your custom workflow{' '}
        </InlineStyled>{' '}
        or a ready-made workflow from {''}
        <InlineStyled $style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          <InlineStyled as='span' $style={{ fontWeight: 600 }} className='app-gradient-text'>
            MikeLegal’s Magic
          </InlineStyled>
          <MagicIconSVG />
        </InlineStyled>
        .
      </StepText>
    ),
    subText: (
      <StepText>
        The Chat Window also shows your{' '}
        <InlineStyled as='span' $style={{ color: '#1F3044', fontWeight: 600 }}>
          most used workflows
        </InlineStyled>{' '}
        for quick access.
      </StepText>
    ),
    image: <img src={WorkflowIntroStep31SVG} alt='step-3 of workflow' />,
    image2: <img src={WorkflowIntroStep32SVG} alt='step-3 of workflow' />,
  },
  {
    stepText: (
      <StepText>
        Run the workflow and{' '}
        <InlineStyled as='span' $style={{ color: '#06F', fontWeight: 600 }}>
          export
        </InlineStyled>{' '}
        the results in{' '}
        <InlineStyled as='span' $style={{ color: '#1F3044', fontWeight: 600 }}>
          Excel
        </InlineStyled>{' '}
        format.
      </StepText>
    ),
    image: <img src={WorkflowIntroStep4SVG} alt='step-4 of workflow' />,
  },
];

const WorkflowIntroModal = ({ open, onCtaClick }) => {
  const { shouldAutoTriggerViewDocChatTour, startViewerDocChatTour } = useTakeTour();

  return (
    <InlineStyled
      as={Modal}
      $style={{
        top: '5vh',
        width: '860px !important',
        '.ant-modal-content': {
          maxHeight: '90vh',
          overflowY: 'auto',
        },
      }}
      destroyOnHidden
      open={open}
      footer={null}
      title={null}
      closeIcon={null}
      maskClosable={false}
    >
      <InlineStyled
        $style={{
          color: '#001529',
          fontSize: '24px',
          fontWeight: 600,
          textAlign: 'center',
          marginBottom: '8px',
        }}
      >
        Introducing{' '}
        <InlineStyled as='span' $style={{ color: '#06F' }}>
          Workflows
        </InlineStyled>
      </InlineStyled>
      <InlineStyled
        $style={{
          color: '#1F3044',
          textAlign: 'center',
          fontSize: '16px',
          fontWeight: 500,
          marginBottom: '16px',
        }}
      >
        Extract data across documents with your custom workflows or {''}
        <InlineStyled $style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          <InlineStyled as='span' className='app-gradient-text'>
            {' '}
            MikeLegal’s Magic
          </InlineStyled>
          <MagicIconSVG />
        </InlineStyled>{' '}
        preset workflows.
      </InlineStyled>

      <InlineStyled
        $style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        {workflowModalData.map((step, i) => (
          <InlineStyled
            // workflowModalData is static data
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            $style={{
              display: 'grid',
              gridTemplateColumns: 'auto 1fr auto',
              columnGap: '12px',
              rowGap: '8px',
              alignItems: 'center',
            }}
          >
            <InlineStyled
              $style={{ color: '#1F3044', fontSize: '14px', fontWeight: 600, whiteSpace: 'nowrap' }}
            >
              Step {i + 1}:
            </InlineStyled>

            <InlineStyled>{step.stepText}</InlineStyled>

            <InlineStyled>{step.image}</InlineStyled>

            {step.subText && (
              <>
                <InlineStyled />

                <InlineStyled
                  $style={{
                    color: '#5A6B7B',
                    fontSize: '13px',
                  }}
                >
                  {step.subText}
                </InlineStyled>

                <InlineStyled>{step.image2}</InlineStyled>
              </>
            )}
          </InlineStyled>
        ))}
      </InlineStyled>
      <InlineStyled $style={{ textAlign: 'center', marginTop: '16px' }}>
        <AppButton
          type='primary'
          size='large'
          onClick={() => {
            oneTimeModal();
            onCtaClick();
            if (shouldAutoTriggerViewDocChatTour) {
              startViewerDocChatTour();
            }
          }}
          $borderRadius='8px'
          $padding='0 24px'
        >
          Ok
        </AppButton>
      </InlineStyled>
    </InlineStyled>
  );
};

export default WorkflowIntroModal;
